import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Upload, Sparkles, CheckCircle2, Loader2, Trash2, AlertTriangle, FileText, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'client',         label: 'Vente (Client)',         color: 'bg-emerald-100 text-emerald-700' },
  { key: 'fournisseur',    label: 'Achat (Fournisseur)',    color: 'bg-orange-100 text-orange-700' },
  { key: 'investissement', label: 'Investissement',          color: 'bg-purple-100 text-purple-700' },
  { key: 'impot',          label: 'Impôt / Taxe',           color: 'bg-rose-100 text-rose-700' },
];

const fmtMoney = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/,/g, ' ');

// Limite de parallélisme pour éviter de saturer la clé LLM
const MAX_PARALLEL = 3;

async function processOne(file) {
  const fd = new FormData();
  fd.append('file', file);
  try {
    // 1) Convertir le PDF en base64 (pour le stocker comme aperçu original)
    const sourcePdfB64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || '').split(',').pop() || '');
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const { data } = await axios.post(`${API}/api/factures/import-pdf`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (data?.success && data.draft) {
      return {
        status: 'done',
        draft: { ...data.draft, source_pdf_b64: sourcePdfB64, source_pdf_filename: file.name },
        error: null,
      };
    }
    return { status: 'error', draft: null, error: 'Réponse IA invalide' };
  } catch (e) {
    return {
      status: 'error',
      draft: null,
      error: e?.response?.data?.detail || e.message || 'Erreur extraction',
    };
  }
}

export default function BulkImportFacturePDFDialog({ open, onOpenChange, onImported }) {
  // items: [{ id, file, status: pending|processing|done|error|saving|saved, draft, error }]
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [savingAll, setSavingAll] = useState(false);

  const reset = () => {
    setItems([]);
    setRunning(false);
    setSavingAll(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const addFiles = (files) => {
    const arr = Array.from(files || []);
    const pdfs = arr.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) {
      toast.error('Aucun PDF sélectionné');
      return;
    }
    if (arr.length !== pdfs.length) {
      toast.warning(`${arr.length - pdfs.length} fichier(s) ignoré(s) (non-PDF)`);
    }
    const next = pdfs.map(f => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      status: 'pending',
      draft: null,
      error: null,
    }));
    setItems(prev => [...prev, ...next]);
  };

  const removeItem = (id) => {
    setItems(prev => prev.filter(it => it.id !== id));
  };

  const updateItem = (id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  };

  const updateDraft = (id, key, val) => {
    setItems(prev => prev.map(it => {
      if (it.id !== id) return it;
      const draft = { ...(it.draft || {}), [key]: val };
      return { ...it, draft };
    }));
  };

  const runAll = async () => {
    setRunning(true);
    const queue = items.filter(it => it.status === 'pending' || it.status === 'error');
    // Marque tous en processing
    setItems(prev => prev.map(it => {
      const inQ = queue.find(q => q.id === it.id);
      return inQ ? { ...it, status: 'processing', error: null } : it;
    }));
    // Worker pool simple
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX_PARALLEL, queue.length) }, async () => {
      while (cursor < queue.length) {
        const myIdx = cursor++;
        const it = queue[myIdx];
        const res = await processOne(it.file);
        if (res.status === 'done') {
          updateItem(it.id, { status: 'done', draft: res.draft, error: null });
        } else {
          updateItem(it.id, { status: 'error', draft: null, error: res.error });
        }
      }
    });
    await Promise.all(workers);
    setRunning(false);
    toast.success('Analyse terminée');
  };

  const saveAll = async () => {
    const toSave = items.filter(it => it.status === 'done' && it.draft);
    if (toSave.length === 0) {
      toast.error('Aucune facture à enregistrer');
      return;
    }
    setSavingAll(true);
    let okCount = 0;
    let errCount = 0;
    for (const it of toSave) {
      updateItem(it.id, { status: 'saving' });
      try {
        const payload = { ...it.draft };
        delete payload.confidence;
        delete payload.raison_categorie;
        delete payload.created_by_hint;
        await axios.post(`${API}/api/factures`, payload);
        updateItem(it.id, { status: 'saved' });
        okCount += 1;
      } catch (e) {
        updateItem(it.id, {
          status: 'error',
          error: e?.response?.data?.detail || e.message || 'Erreur enregistrement',
        });
        errCount += 1;
      }
    }
    setSavingAll(false);
    if (okCount > 0) toast.success(`${okCount} facture(s) enregistrée(s)`);
    if (errCount > 0) toast.error(`${errCount} erreur(s)`);
    onImported?.();
    if (errCount === 0) close();
  };

  const stats = {
    total: items.length,
    done: items.filter(it => it.status === 'done').length,
    saved: items.filter(it => it.status === 'saved').length,
    err: items.filter(it => it.status === 'error').length,
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="bulk-import-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            Import en masse de factures PDF
          </DialogTitle>
          <DialogDescription>
            Sélectionne plusieurs PDFs — Gemini 3 Pro les analyse en parallèle et classe chaque facture automatiquement. Tu valides la liste en une seule action.
          </DialogDescription>
        </DialogHeader>

        {/* Zone d'ajout */}
        <label
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-emerald-300 rounded-xl p-6 cursor-pointer hover:bg-emerald-50 transition"
          data-testid="bulk-dropzone"
        >
          <Upload className="h-7 w-7 text-emerald-600" />
          <p className="font-semibold text-slate-700 text-sm">Cliquer ou glisser-déposer plusieurs PDFs</p>
          <p className="text-[11px] text-slate-500">15 Mo max par fichier. Traitement par lots de {MAX_PARALLEL} en parallèle.</p>
          <input
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
            data-testid="bulk-input"
          />
        </label>

        {/* Stats */}
        {items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="outline">{stats.total} fichier(s)</Badge>
            {stats.done > 0 && <Badge className="bg-emerald-100 text-emerald-700">{stats.done} analysé(s)</Badge>}
            {stats.saved > 0 && <Badge className="bg-blue-100 text-blue-700">{stats.saved} enregistré(s)</Badge>}
            {stats.err > 0 && <Badge className="bg-rose-100 text-rose-700">{stats.err} erreur(s)</Badge>}
          </div>
        )}

        {/* Liste */}
        <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {items.length === 0 && (
            <p className="text-xs text-slate-400 italic text-center py-6">
              Aucun fichier ajouté pour le moment.
            </p>
          )}
          {items.map((it) => (
            <Card
              key={it.id}
              className={`border ${it.status === 'error' ? 'border-rose-300 bg-rose-50/40' : it.status === 'saved' ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200'}`}
              data-testid={`bulk-row-${it.id}`}
            >
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 mt-0.5 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{it.file.name}</p>
                    <p className="text-[10px] text-slate-500">{(it.file.size / 1024).toFixed(0)} Ko</p>
                  </div>
                  {it.status === 'pending'    && <Badge variant="outline" className="text-[10px]">En attente</Badge>}
                  {it.status === 'processing' && <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1"><Loader2 className="h-3 w-3 animate-spin"/>Analyse…</Badge>}
                  {it.status === 'done'       && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Prêt</Badge>}
                  {it.status === 'saving'     && <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1"><Loader2 className="h-3 w-3 animate-spin"/>Enreg…</Badge>}
                  {it.status === 'saved'      && <Badge className="bg-blue-100 text-blue-700 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1"/>Enregistré</Badge>}
                  {it.status === 'error'      && <Badge className="bg-rose-100 text-rose-700 text-[10px] gap-1"><AlertTriangle className="h-3 w-3"/>Erreur</Badge>}
                  {(it.status === 'pending' || it.status === 'error') && !running && !savingAll && (
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem(it.id)} data-testid={`bulk-remove-${it.id}`}>
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>

                {it.error && (
                  <p className="text-[11px] text-rose-700 italic">{it.error}</p>
                )}

                {it.status === 'done' && it.draft && (
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2 text-xs">
                    <Input
                      className="md:col-span-2 h-8 text-xs"
                      value={it.draft.numero || ''}
                      onChange={(e) => updateDraft(it.id, 'numero', e.target.value)}
                      placeholder="N°"
                      data-testid={`bulk-numero-${it.id}`}
                    />
                    <Input
                      className="md:col-span-2 h-8 text-xs"
                      type="date"
                      value={it.draft.date_facture || ''}
                      onChange={(e) => updateDraft(it.id, 'date_facture', e.target.value)}
                    />
                    <Input
                      className="md:col-span-3 h-8 text-xs"
                      value={it.draft?.client?.nom || ''}
                      onChange={(e) => updateDraft(it.id, 'client', { ...(it.draft.client || {}), nom: e.target.value })}
                      placeholder="Contre-partie"
                    />
                    <Input
                      className="md:col-span-2 h-8 text-xs text-right font-semibold"
                      type="number"
                      value={it.draft.total_ttc || 0}
                      onChange={(e) => updateDraft(it.id, 'total_ttc', Number(e.target.value))}
                    />
                    <Select
                      value={it.draft.categorie || 'client'}
                      onValueChange={(v) => updateDraft(it.id, 'categorie', v)}
                    >
                      <SelectTrigger className="md:col-span-3 h-8 text-xs" data-testid={`bulk-cat-${it.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    {it.draft.raison_categorie && (
                      <p className="md:col-span-12 text-[10px] text-slate-500 italic">
                        💡 {it.draft.raison_categorie}
                        {it.draft.confidence != null && (
                          <span className="ml-1 text-slate-400">— confiance {Math.round((it.draft.confidence || 0) * 100)}%</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {it.status === 'saved' && it.draft && (
                  <p className="text-[11px] text-blue-700">
                    {it.draft.numero} — {fmtMoney(it.draft.total_ttc)} FCFA — {CATEGORIES.find(c => c.key === it.draft.categorie)?.label}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={close} disabled={running || savingAll}>
            Fermer
          </Button>
          {items.some(it => it.status === 'pending' || it.status === 'error') && (
            <Button
              onClick={runAll}
              disabled={running || savingAll}
              className="bg-amber-600 hover:bg-amber-700 gap-2"
              data-testid="bulk-run-all"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Analyser tout ({items.filter(it => it.status === 'pending' || it.status === 'error').length})
            </Button>
          )}
          {stats.done > 0 && (
            <Button
              onClick={saveAll}
              disabled={running || savingAll}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="bulk-save-all"
            >
              {savingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Tout enregistrer ({stats.done})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
